<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page"><span>Traffic Fines</span></li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-header d-flex justify-content-between align-items-center">
                        <h3>Traffic Fines</h3>
                        <button v-if="hasAddTrafficFinesPermission" @click="fetchAllVehicleFines"
                            class="btn btn-success">
                            Add Vehicle Fines
                        </button>
                    </div>

                    <div class="panel-body">
                        <!-- Main Search and Filters -->
                        <!-- First Row: Search and Date Filters -->
                        <div class="row g-3 mb-2">
                            <!-- Search by Plate -->
                            <div class="col-md-6 col-lg-4">
                                <div class="input-group">
                                    <input v-model="searchPlate" type="text" class="form-control"
                                        placeholder="Search by vehicle plate (e.g. T810EJK)"
                                        @keyup.enter="searchVehicleFines">
                                    <button @click="searchVehicleFines" class="btn btn-primary"
                                        :disabled="searchLoading || !searchPlate.trim()">
                                        <span v-if="!searchLoading">Search</span>
                                        <span v-else class="spinner-border spinner-border-sm"></span>
                                    </button>
                                </div>
                            </div>

                            <!-- General Search (Vehicle, Route, Driver) -->
                            <div class="col-md-6 col-lg-4">
                                <input v-model="searchQuery" type="text" class="form-control"
                                    placeholder="Search by vehicle, route, driver...">
                            </div>

                            <!-- Date Filter -->
                            <div class="col-md-12 col-lg-4">
                                <div class="input-group">
                                    <input type="date" v-model="startDate" class="form-control" placeholder="From">
                                    <input type="date" v-model="endDate" class="form-control" placeholder="To">
                                </div>
                            </div>
                        </div>

                        <!-- Second Row: Filters -->
                        <div class="row g-3 mb-4">
                            <!-- Penalty Filter -->
                            <div class="col-md-6 col-lg-3">
                                <select v-model="penaltyFilter" class="form-select">
                                    <option value="all">All Penalties</option>
                                    <option value="with">With Penalties</option>
                                    <option value="without">Without Penalties</option>
                                </select>
                            </div>

                            <!-- Many Fines Filter -->
                            <div class="col-md-6 col-lg-3">
                                <select v-model="manyFinesFilter" class="form-select">
                                    <option value="">All Vehicles</option>
                                    <option value="many">Vehicles with Many Fines</option>
                                </select>
                            </div>

                            <!-- Status Filter -->
                            <div class="col-md-6 col-lg-2">
                                <select v-model="statusFilter" class="form-select">
                                    <option value="">All Status</option>
                                    <option value="PENDING">Pending</option>
                                    <option value="PAID">Paid</option>
                                </select>
                            </div>

                            <!-- Print Button -->
                            <div class="col-md-3 col-lg-2">
                                <button v-if="hasPrintTrafficFinesPermission"
                                    class="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                                    @click="printTrafficFinesSummaryReport">
                                    Print Report
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="feather feather-printer">
                                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                                        <path
                                            d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2">
                                        </path>
                                        <rect x="6" y="14" width="12" height="8"></rect>
                                    </svg>
                                </button>
                            </div>

                            <!-- Clear Filters Button -->
                            <div class="col-md-3 col-lg-2">
                                <button class="btn btn-danger w-100" @click="clearFilters">
                                    Clear Filters
                                </button>
                            </div>
                        </div>

                        <!-- Items per page selector -->
                        <div class="d-flex align-items-center mb-3">
                            <span class="me-2">Fines per page:</span>
                            <select v-model="perPage" class="form-select form-select-sm" style="width: 80px;">
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>

                        <!-- Search Results Summary -->
                        <div v-if="searchResults" class="alert alert-info mb-4">
                            <div class="d-flex flex-wrap justify-content-between align-items-center">
                                <h6 class="mb-0">Results for: <strong>{{ searchResults.plate_number }}</strong></h6>
                                <div class="d-flex gap-3 mt-2 mt-md-0">
                                    <span class="badge bg-info">Pending: TZS {{ searchResults.total_pending }}</span>
                                    <span class="badge bg-success">Paid: TZS {{ searchResults.total_paid }}</span>
                                    <span class="badge bg-danger">Unpaid: TZS {{ searchResults.total_unpaid }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="text-center my-5 py-5">
                            <div class="spinner-border text-primary" style="width: 3rem; height: 3rem;" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <p class="mt-3">Loading traffic fines...</p>
                        </div>

                        <!-- Main Table -->
                        <div v-else class="table-responsive">
                            <table class="table table-hover table-bordered">
                                <thead class="table-light">
                                    <tr>
                                        <th class="d-none d-md-table-cell">S/N</th>
                                        <th>Vehicle</th>
                                        <th class="d-none d-md-table-cell">Route</th>
                                        <th>Driver</th>
                                        <th class="d-none d-lg-table-cell">Control No</th>
                                        <th>Location</th>
                                        <th class="d-none d-lg-table-cell">Offence</th>
                                        <th>Charge</th>
                                        <th class="d-none d-md-table-cell">Penalty</th>
                                        <th class="d-none d-md-table-cell">Penalty Date</th>
                                        <th>Status</th>
                                        <th>Issued Date</th>
                                        <th class="d-none d-md-table-cell">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in paginatedTrafficFines" :key="item.reference">
                                        <td>
                                            <!-- <td class="d-none d-md-table-cell"> -->
                                            {{ (currentPage - 1) * perPage + index + 1 }}
                                        </td>
                                        <td>{{ item.plate_number }}</td>
                                        <td>
                                            <!-- <td class="d-none d-md-table-cell"> -->
                                            {{ item.assigned_route }}
                                        </td>
                                        <td>{{ item.driver_name }}</td>
                                        <td>
                                            <!-- <td class="d-none d-lg-table-cell"> -->
                                            {{ item.reference }}
                                        </td>
                                        <td>{{ item.location }}</td>
                                        <td>
                                            <!-- <td class="d-none d-lg-table-cell"> -->
                                            {{ item.offence }}
                                        </td>
                                        <td>TZS {{ formatCurrency(item.charge) }}</td>
                                        <td>
                                            <!-- <td class="d-none d-md-table-cell"> -->
                                            TZS {{ item.penalty }}
                                        </td>
                                        <td>
                                            <!-- <td class="d-none d-md-table-cell"> -->
                                            {{ formatDate(item.pendate) }}
                                        </td>
                                        <td>
                                            <span :class="`badge bg-${getStatusClass(item.status)}`">
                                                {{ item.status }}
                                            </span>
                                        </td>
                                        <td>
                                            <!-- <td class="d-none d-md-table-cell"> -->
                                            {{ formatDate(item.issued_date) }}
                                        </td>
                                        <td class="text-center">
                                            <button v-if="hasEditTrafficFinesPermission" @click="openEditModal(item)"
                                                class="btn btn-primary btn-sm mb-2">
                                                Edit
                                            </button>
                                            <button v-if="hasPrintTrafficFinesPermission" @click="printReport(item)"
                                                class="btn btn-sm btn-info mr-2 mb-2" title="Print">
                                                Print
                                            </button>
                                            <button v-if="hasDeleteTrafficFinesPermission" @click="deleteItem(item)"
                                                class="btn btn-sm btn-danger mb-2" title="Delete">
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Empty State -->
                        <div v-if="!isLoading && filteredTrafficFines.length === 0" class="text-center py-5">
                            <div class="empty-state-icon">
                                <i class="fas fa-car-crash fa-3x text-muted"></i>
                            </div>
                            <h5 class="mt-3">No traffic fines found</h5>
                            <p class="text-muted">Try adjusting your search or filters</p>
                        </div>

                        <!-- Pagination Links -->
                        <div class="paginating-container pagination-solid flex-column align-items-center">
                            <ul class="pagination mb-4 b-pagination">
                                <li class="page-item first" :class="{ disabled: currentPage === 1 }">
                                    <button class="page-link" @click="goToPage(1)">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                            viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                        </svg>
                                    </button>
                                </li>
                                <li class="page-item prev" :class="{ disabled: currentPage === 1 }">
                                    <button class="page-link" @click="goToPage(currentPage - 1)">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                            viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                </li>

                                <li v-for="page in pagesToShow" :key="page" class="page-item"
                                    :class="{ active: page === currentPage }">
                                    <button class="page-link" @click="goToPage(page)">{{ page }}</button>
                                </li>

                                <li class="page-item next" :class="{ disabled: currentPage === totalPages }">
                                    <button class="page-link" @click="goToPage(currentPage + 1)">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                            viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </li>
                                <li class="page-item last" :class="{ disabled: currentPage === totalPages }">
                                    <button class="page-link" @click="goToPage(totalPages)">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                            viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add Fines Modal -->
        <div v-if="showModal" class="modal fade show" tabindex="-1" style="display: block; background: rgba(0,0,0,0.5)">
            <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header text-white">
                        <h5 class="modal-title">Add Vehicle Traffic Fines</h5>
                        <button type="button" class="btn-close btn-close-white" @click="closeModal"></button>
                    </div>
                    <div class="modal-body">
                        <!-- Search Section -->
                        <div class="row g-3 mb-4">
                            <div class="col-md-8">
                                <label class="form-label">Search Vehicle</label>
                                <div class="input-group">
                                    <input v-model="vehiclePlate" type="text" class="form-control"
                                        placeholder="Enter vehicle plate (e.g. T810EJK)">
                                    <button @click="fetchVehicleFines" class="btn btn-primary"
                                        :disabled="isSearching || !vehiclePlate.trim()">
                                        <span v-if="!isSearching">Search</span>
                                        <span v-else class="spinner-border spinner-border-sm"></span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Total Pending -->
                        <div v-if="totalPendingAmount" class="alert alert-warning">
                            <div class="d-flex justify-content-between align-items-center">
                                <strong>Total Pending Amount:</strong>
                                <span class="fw-bold">TZS {{ formatCurrency(totalPendingAmount) }}</span>
                            </div>
                        </div>

                        <!-- Vehicle Details -->
                        <div v-if="trafficFines.length > 0" class="row g-3 mb-4">
                            <div class="col-md-6">
                                <label class="form-label">Plate Number</label>
                                <input type="text" class="form-control" v-model="vehiclePlate" readonly>
                            </div>
                            <div class="col-md-6">
                                <label class="form-label">Assigned Route</label>
                                <input type="text" class="form-control" v-model="assignedRoute">
                            </div>
                            <div class="col-12">
                                <label class="form-label">Driver</label>
                                <Multiselect v-model="selectedDriver" :options="filteredDrivers" :searchable="true"
                                    placeholder="Select driver" label="name" track-by="id">
                                    <template v-slot:option="{ option }">
                                        <div class="d-flex justify-content-between align-items-center">
                                            <span>{{ option.name }}</span>
                                            <small class="text-muted">{{ option.phone }}</small>
                                        </div>
                                    </template>
                                </Multiselect>
                            </div>
                        </div>

                        <!-- Fines Table -->
                        <div v-if="trafficFines.length > 0" class="table-responsive mb-4">
                            <table class="table table-sm table-bordered">
                                <thead class="table-light">
                                    <tr>
                                        <th>Reference</th>
                                        <th>Offence</th>
                                        <th>Location</th>
                                        <th>Amount</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="fine in trafficFines" :key="fine.reference">
                                        <td>{{ fine.reference }}</td>
                                        <td>{{ fine.offence }}</td>
                                        <td>{{ fine.location }}</td>
                                        <td>TZS {{ formatCurrency(fine.charge) }}</td>
                                        <td>{{ formatDate(fine.issued_date) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div v-else-if="searched && !isSearching" class="text-center py-4">
                            <i class="fas fa-search fa-2x text-muted mb-3"></i>
                            <p>No pending fines found for this vehicle</p>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
                        <button type="button" class="btn btn-primary" @click="saveVehicleFines"
                            :disabled="isSaving || trafficFines.length === 0 || !selectedDriver || !assignedRoute">
                            <span v-if="!isSaving">Save Fines</span>
                            <span v-else class="spinner-border spinner-border-sm"></span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Edit Fines Modal -->
        <div v-if="showEditModal" class="modal fade show" tabindex="-1"
            style="display: block; background: rgba(0,0,0,0.5)">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header text-white">
                        <h5 class="modal-title">Edit Traffic Fine</h5>
                        <button type="button" class="btn-close btn-close-white" @click="closeEditModal"></button>
                    </div>
                    <div class="modal-body">
                        <div class="row g-3">
                            <!-- Read-only fields -->
                            <div class="col-md-6">
                                <label class="form-label">Plate Number</label>
                                <input type="text" class="form-control" v-model="editingItem.plate_number" readonly>
                            </div>
                            <div class="col-md-6">
                                <label class="form-label">Reference</label>
                                <input type="text" class="form-control" v-model="editingItem.reference" readonly>
                            </div>
                            <div class="col-md-6">
                                <label class="form-label">Offence</label>
                                <input type="text" class="form-control" v-model="editingItem.offence" readonly>
                            </div>
                            <div class="col-md-6">
                                <label class="form-label">Location</label>
                                <input type="text" class="form-control" v-model="editingItem.location" readonly>
                            </div>
                            <div class="col-md-6">
                                <label class="form-label">Charge</label>
                                <input type="text" class="form-control" v-model="editingItem.charge" readonly>
                            </div>
                            <div class="col-md-6">
                                <label class="form-label">Penalty</label>
                                <input type="text" class="form-control" v-model="editingItem.penalty" readonly>
                            </div>

                            <!-- Editable fields -->
                            <div class="col-md-6">
                                <label class="form-label">Assigned Route</label>
                                <input type="text" class="form-control" v-model="editingItem.assigned_route">
                            </div>
                            <div class="col-md-6">
                                <label class="form-label">Driver</label>
                                <Multiselect v-model="editingItem.driver_id" :options="filteredDrivers"
                                    :searchable="true" :close-on-select="true" :clear-on-select="false"
                                    :preserve-search="true" placeholder="Select driver" label="name" track-by="id"
                                    @search-change="driverSearch = $event" />
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeEditModal">Cancel</button>
                        <button type="button" class="btn btn-primary" @click="saveEditedFine" :disabled="isSaving">
                            <span v-if="!isSaving">Save Changes</span>
                            <span v-else class="spinner-border spinner-border-sm"></span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}

.panel-header {
    padding: 20px;
    border-bottom: 1px solid #e0e6ed;
}

.badge {
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
}

.badge-paid {
    background-color: #d1fae5;
    color: #065f46;
}

.badge-unpaid {
    background-color: #fee2e2;
    color: #b91c1c;
}

.badge-pending {
    background-color: #fef3c7;
    color: #92400e;
}

.modal {
    backdrop-filter: blur(5px);
}

.driver-option {
    display: flex;
    justify-content: space-between;
    width: 100%;
}

@media (max-width: 768px) {
    .table-responsive {
        border: 0;
    }

    .table-responsive table {
        display: block;
    }

    .table-responsive thead {
        display: none;
    }

    .table-responsive tr {
        margin-bottom: 1rem;
        display: block;
        border: 1px solid #dee2e6;
        border-radius: 0.25rem;
    }

    .table-responsive td {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75rem;
        border-bottom: 1px solid #dee2e6;
    }

    .table-responsive td::before {
        content: attr(data-label);
        font-weight: bold;
        margin-right: 1rem;
    }
}

/* Badge colors */
.badge.bg-paid {
    background-color: #28a745 !important;
}

.badge.bg-unpaid {
    background-color: #dc3545 !important;
}

.badge.bg-pending {
    background-color: #ffc107 !important;
    color: #212529 !important;
}

/* Modal improvements */
.modal-content {
    border: none;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

/* Multiselect customization */
:deep(.multiselect) {
    min-height: 38px;
}

:deep(.multiselect__tags) {
    min-height: 38px;
    padding: 6px 40px 0 8px;
    border: 1px solid #ced4da;
}

:deep(.multiselect__select) {
    height: 36px;
}

:deep(.multiselect__placeholder) {
    margin-bottom: 4px;
    padding-top: 4px;
}
</style>

<script setup>
import { onMounted, ref, computed, watch } from 'vue';
import { useMeta } from '@/composables/use-meta';
import axios from 'axios';
import { useStore } from 'vuex';
import Swal from 'sweetalert2';
import { BASE_URL, BASE_URL_3 } from '@/Config/config';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

useMeta({ title: 'Traffic Fines' });

const store = useStore();

// Reactive Variables
const trafficFines = ref([]);
const vehiclePlate = ref('');
const totalPendingAmount = ref(0);
const drivers = ref([]);
const assignedRoute = ref('');
const selectedDriver = ref(null);
const vehicleFines = ref([]);
const editingItem = ref({
    id: null,
    plate_number: '',
    reference: '',
    offence: '',
    location: '',
    charge: '',
    penalty: '',
    assigned_route: '',
    driver_id: null,
    driver_name: ''
});

// Modal states
const showModal = ref(false);
const showEditModal = ref(false);

// Loading states
const isLoading = ref(false);
const searchLoading = ref(false);
const isSearching = ref(false);
const isSaving = ref(false);
const isFetching = ref(false);

// Pagination
const currentPage = ref(1);
const perPage = ref(20);

// Filters
const statusFilter = ref('');
const manyFinesFilter = ref('');
const searchQuery = ref('');
const startDate = ref('');
const endDate = ref('');
const penaltyFilter = ref('all');
const driverSearch = ref('');
const searched = ref(false);
const searchPlate = ref('');
const searchResults = ref(null);

// Permission Check
const hasPrintTrafficFinesPermission = computed(() => store.getters.hasPermission('print_abs_unpaid_traffic_fines'));
const hasAddTrafficFinesPermission = computed(() => store.getters.hasPermission('add_abs_traffic_fines'));
const hasEditTrafficFinesPermission = computed(() => store.getters.hasPermission('edit_abs_traffic_fines'));
const hasDeleteTrafficFinesPermission = computed(() => store.getters.hasPermission('delete_abs_traffic_fines'));

// Get username from store
const username = computed(() => {
    const user = store.state.user;
    return user?.username || user?.name || 'User';
});

// SweetAlert Configuration
const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6'
    });
};

const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
    });
};

const showConfirmationAlert = (message) => {
    return Swal.fire({
        title: 'Are you sure?',
        text: message,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
    });
};

// Computed Property for Filtered Data
const filteredTrafficFines = computed(() => {
    let filtered = [...trafficFines.value];

    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(item =>
            item.plate_number.toLowerCase().includes(query) ||
            (item.driver_name && item.driver_name.toLowerCase().includes(query)) ||
            (item.assigned_route && item.assigned_route.toLowerCase().includes(query))
        );
    }

    if (penaltyFilter.value === 'with') {
        filtered = filtered.filter(item => parseFloat(item.penalty) > 0);
    } else if (penaltyFilter.value === 'without') {
        filtered = filtered.filter(item => parseFloat(item.penalty) <= 0);
    }

    if (statusFilter.value) {
        filtered = filtered.filter(item => item.status?.toUpperCase() === statusFilter.value);
    }

    if (manyFinesFilter.value === 'many') {
        const countMap = {};
        filtered.forEach(f => {
            countMap[f.plate_number] = (countMap[f.plate_number] || 0) + 1;
        });
        filtered = filtered.filter(f => countMap[f.plate_number] > 3); // threshold of "many"
    }

    if (startDate.value) {
        filtered = filtered.filter(item =>
            new Date(item.issued_date) >= new Date(startDate.value)
        );
    }

    if (endDate.value) {
        filtered = filtered.filter(item =>
            new Date(item.issued_date) <= new Date(endDate.value)
        );
    }

    return filtered;
});

const paginatedTrafficFines = computed(() => {
    const start = (currentPage.value - 1) * perPage.value;
    const end = start + perPage.value;
    return filteredTrafficFines.value.slice(start, end);
});

const totalPages = computed(() => {
    return Math.ceil(filteredTrafficFines.value.length / perPage.value);
});

const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
};

const pagesToShow = computed(() => {
    const total = totalPages.value;
    const current = currentPage.value;
    const range = 5;

    let start = Math.max(current - Math.floor(range / 2), 1);
    let end = start + range - 1;

    if (end > total) {
        end = total;
        start = Math.max(1, end - range + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

watch([filteredTrafficFines, perPage.value], () => {
    currentPage.value = 1;
});

// Edit modal functions
const openEditModal = (item) => {
    editingItem.value = {
        id: item.id,
        plate_number: item.plate_number,
        reference: item.reference,
        offence: item.offence,
        location: item.location,
        charge: item.charge,
        penalty: item.penalty,
        assigned_route: item.assigned_route,
        driver_id: item.driver_id,
        driver_name: item.driver_name
    };
    showEditModal.value = true;
};

const closeEditModal = () => {
    showEditModal.value = false;
};

const saveEditedFine = async () => {
    try {
        isSaving.value = true;

        // Get the driver ID correctly
        const driver = editingItem.value.driver_id;
        const driverId = typeof driver === 'object' && driver !== null ? driver.id : driver;

        // Find the selected driver's name
        const selectedDriver = drivers.value.find(d => d.id === driverId);

        const payload = {
            assigned_route: editingItem.value.assigned_route,
            driver_id: driverId,
            driver_name: selectedDriver ? selectedDriver.name : ''
        };

        console.log('Payload to be sent:', payload);
        console.log('Traffic fine id:', editingItem.value.id);

        const response = await axios.put(
            `${BASE_URL}/traffic-fines/update/${editingItem.value.id}`,
            payload
        );

        if (response.data.status === 'success') {
            showSuccessAlert('Fine updated successfully');
            await loadTrafficFines();
            closeEditModal();
        }
    } catch (error) {
        console.error('Error updating fine:', error);
        showErrorAlert('Failed to update fine');
    } finally {
        isSaving.value = false;
    }
};

// Helper Functions
const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
        case 'paid': return 'paid';
        case 'unpaid': return 'unpaid';
        case 'pending': return 'pending';
        default: return 'secondary';
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '—';
    const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    return new Date(dateString).toLocaleString(undefined, options);
};

// Format currency (Tanzanian Shillings)
const formatCurrency = (amount) => {
    return parseFloat(amount).toLocaleString('en-TZ', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

const deleteItem = async (item) => {
    const result = await showConfirmationAlert(
        `Are you sure you want to delete fine ${item.reference} for vehicle ${item.plate_number}?`
    );

    if (result.isConfirmed) {
        try {
            await axios.delete(`${BASE_URL}/traffic-fines/destroy/${item.id}`);
            showSuccessAlert('Fine deleted successfully');
            await loadTrafficFines();
        } catch (error) {
            console.error('Error deleting fine:', error);
            showErrorAlert('Failed to delete fine');
        }
    }
};

// Print function
const printReport = (item) => {
    const printWindow = window.open('', '_blank');

    const printContent = `
        <html>
            <head>
                <title>Traffic Fine Report - ${item.plate_number}</title>
                <style>
                    @page { size: A4 landscape; margin: 10mm; }
                    body {
                        font-family: Arial, sans-serif;
                        font-size: 13px;
                        padding: 20px;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 25px;
                    }
                    .header .title {
                        font-size: 22px;
                        font-weight: bold;
                        color: red;
                    }
                    .header .subtitle {
                        font-size: 14px;
                        margin-top: 5px;
                    }
                    .report-title {
                        font-size: 16px;
                        font-weight: bold;
                        text-align: center;
                        margin: 10px 0;
                        text-decoration: underline;
                    }
                    .details {
                        margin-bottom: 20px;
                    }
                    .details div {
                        margin: 5px 0;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 15px;
                    }
                    th, td {
                        border: 1px solid #000;
                        padding: 10px;
                        text-align: left;
                    }
                    th {
                        background-color: #f8f9fa;
                        font-weight: bold;
                    }
                    .footer {
                        margin-top: 40px;
                        display: flex;
                        justify-content: space-between;
                        flex-wrap: wrap;
                        font-size: 13px;
                    }
                    .signature {
                        margin-top: 50px;
                        text-align: center;
                        min-width: 200px;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="title">ABOOD BUS SERVICE LIMITED</div>
                    <div class="subtitle">P.O Box 127 Msamvu Morogoro, Tanzania</div>
                    <div class="subtitle">Mob: +255 (0) 23 261 3589</div>
                </div>

                <div class="report-title">TRAFFIC FINE REPORT</div>

                <div class="details">
                    <div><strong>Issued Date:</strong> ${formatDate(item.issued_date)}</div>
                    <div><strong>Plate Number:</strong> ${item.plate_number}</div>
                    <div><strong>Reference (Control No):</strong> ${item.reference}</div>
                    <div><strong>Driver:</strong> ${item.driver_name || 'N/A'}</div>
                    <div><strong>Route:</strong> ${item.assigned_route || 'N/A'}</div>
                </div>

                <table>
                    <thead>
                        <tr>
                            <th>Offence</th>
                            <th>Location</th>
                            <th>Charge</th>
                            <th>Penalty</th>
                            <th>Status</th>
                            <th>Penalty Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>${item.offence}</td>
                            <td>${item.location}</td>
                            <td>TZS ${formatCurrency(item.charge)}</td>
                            <td>TZS ${formatCurrency(item.penalty)}</td>
                            <td>${item.status}</td>
                            <td>${formatDate(item.pendate)}</td>
                        </tr>
                    </tbody>
                </table>

                <div class="footer">
                    <div><strong>Total Amount:</strong> TZS ${formatCurrency(parseFloat(item.charge) + parseFloat(item.penalty))}</div>

                    <div class="signature">
                        _________________________<br>
                        Authorized Signature
                    </div>
                </div>
            </body>
        </html>
    `;

    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();

    printWindow.onload = () => {
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 500);
    };
};


const printTrafficFinesSummaryReport = () => {
    const dataToPrint = filteredTrafficFines.value; // Or use paginatedTrafficFines.value if you want full data
    if (!dataToPrint.length) return;

    const reportTitle = 'Traffic Fines Summary Report';
    const selectedStatus = statusFilter.value ? `Status: ${statusFilter.value}` : '';
    const penaltyStatus = penaltyFilter.value === 'with' ? 'With Penalties'
        : penaltyFilter.value === 'without' ? 'Without Penalties' : '';
    const dateRange = startDate.value && endDate.value ? `Date: ${startDate.value} to ${endDate.value}` : '';

    let headerInfo = [selectedStatus, penaltyStatus, dateRange].filter(Boolean).join(' | ');

    let tableRows = dataToPrint.map((item, index) => `
        <tr>
            <td>${index + 1}</td>
            <td>${item.plate_number}</td>
            <td>${item.reference}</td>
            <td>${item.location}</td>
            <td>${item.offence}</td>
            <td>TZS ${formatCurrency(item.charge)}</td>
            <td>${item.penalty}</td>
            <td>${formatDate(item.issued_date)}</td>
            <td>${formatDate(item.pendate)}</td>
        </tr>
    `).join('');

    const printWindow = window.open('', '', 'width=900,height=1000');
    printWindow.document.write(`
        <html>
            <head>
                <title>${reportTitle}</title>
                <style>
                    @page { size: A4 portrait; margin: 10pt; }
                    body {
                        font-family: Arial, sans-serif;
                        font-size: 12px;
                        padding: 15px;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 20px;
                    }
                    .header .title {
                        font-size: 24px;
                        font-weight: bold;
                        color: red;
                    }
                    .sub-header {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 10px;
                        flex-wrap: wrap;
                        font-weight: bold;
                    }
                    .report-title {
                        font-size: 18px;
                        font-weight: bold;
                        text-decoration: underline;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 20px;
                    }
                    th, td {
                        border: 1px solid #000;
                        padding: 8px;
                        text-align: left;
                    }
                    th {
                        background-color: #f8f9fa;
                        font-weight: bold;
                    }
                    td { font-size: 12px; }
                    .footer {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-top: 40px;
                        border-top: 2px solid #000;
                        padding-top: 10px;
                        font-size: 12px;
                        flex-wrap: wrap;
                        gap: 10px;
                    }
                    .footer > div { min-width: 18%; }
                    .print-actions {
                        display: flex;
                        justify-content: center;
                        margin: 20px 0;
                        gap: 10px;
                    }
                    button {
                        padding: 8px 16px;
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;
                        font-weight: bold;
                    }
                    .btn-print { background-color: #4CAF50; color: white; }
                    .btn-pdf { background-color: #2196F3; color: white; }
                    .btn-close { background-color: #f44336; color: white; }
                    @media print {
                        .print-actions { display: none !important; }
                    }
                    tr { page-break-inside: avoid; }
                </style>
                <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"><\/script>
            </head>
            <body>
                <div id="content-to-export">
                    <div class="header">
                        <div class="title">ABOOD BUS SERVICE LIMITED</div>
                        <div>P.O Box 127 Msamvu Morogoro, Tanzania</div>
                        <div>Mob: +255 (0) 23 261 3589</div>
                    </div>
                    <div class="sub-header">
                        <div class="report-title">${reportTitle}</div>
                        <div>${headerInfo}</div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>S/N</th>
                                <th>Vehicle</th>
                                <th>Control No</th>
                                <th>Location</th>
                                <th>Offence</th>
                                <th>Charge</th>
                                <th>Penalty</th>
                                <th>Issued Date</th>
                                <th>Penalty Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${tableRows}
                        </tbody>
                    </table>
                    <div class="footer">
                        <div>Prepared By: ${username.value}<br>IT Officer</div>
                        <div>____________________<br>HR</div>
                        <div>_____________________<br>Accounts</div>
                        <div>_____________________<br>GM/MD</div>
                    </div>
                </div>
                <div class="print-actions">
                    <button onclick="window.print()" class="btn-print">Print Report</button>
                    <button onclick="downloadPDF()" class="btn-pdf">Download as PDF</button>
                    <button onclick="window.close()" class="btn-close">Close</button>
                </div>
                <script>
                    function downloadPDF() {
                        const element = document.getElementById('content-to-export');
                        const opt = {
                            margin: 10,
                            filename: 'traffic_fines_summary_report.pdf',
                            image: { type: 'jpeg', quality: 0.98 },
                            html2canvas: { scale: 2, logging: true, useCORS: true },
                            jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
                        };
                        const originalText = document.querySelector('.btn-pdf').textContent;
                        document.querySelector('.btn-pdf').textContent = 'Generating PDF...';
                        html2pdf().set(opt).from(element).save().then(() => {
                            document.querySelector('.btn-pdf').textContent = originalText;
                        });
                    }
                    window.onload = function() { window.focus(); };
                <\/script>
            </body>
        </html>
    `);

    printWindow.document.close();
    printWindow.focus();
};

// Fetch traffic fines from the DB on component mount
const loadTrafficFines = async () => {
    try {
        isLoading.value = true;
        const response = await axios.get(`${BASE_URL}/traffic-fines/get`);
        trafficFines.value = response.data?.data || response.data || [];
        console.log('Traffic Fines responded:', response.status);
        // console.log('Traffic Fines loaded:', trafficFines.value);
    } catch (error) {
        console.error('Error fetching traffic fines:', error);
        showErrorAlert('Failed to fetch traffic fines. Please try again later.');
    } finally {
        isLoading.value = false;
    }
};

const fetchAllVehicleFines = async () => {
    try {
        isFetching.value = true
        const response = await axios.post(`${BASE_URL}/traffic-fines/fetch-all`);
        vehicleFines.value = response.data.total_vehicles || [];
        showSuccessAlert('The server is fetching All ehicles fines from TMS website! Seat and relax while its saving the data. It takes up to 5 minutes to process the data.');
    } catch (error) {
        console.log('Failed to fetch Vehicle fines from TMS Website', error);
        showErrorAlert('Failed to fetch Vehicle fines from TMS Website');
    } finally {
        isFetching.value = false;
    }
};

const loadDrivers = async () => {
    try {
        const response = await axios.get(`${BASE_URL_3}/company/employees/list?company_id=2`);
        drivers.value = response.data.employees || response;
        console.log("Drivers response:", response.status);
    } catch (error) {
        console.error('Error fetching employees:', error);
    }
};

const filteredDrivers = computed(() => {
    if (!driverSearch.value) return drivers.value;
    const term = driverSearch.value.toLowerCase();
    return drivers.value.filter(driver =>
        driver.name.toLowerCase().includes(term))
});

// Save fines to your backend
const saveVehicleFines = async () => {
    try {
        isSaving.value = true;

        const payload = {
            vehicle: vehiclePlate.value.toUpperCase(),
            assigned_route: assignedRoute.value,
            driver_id: selectedDriver.value.id,
            driver_name: selectedDriver.value.name,
            fines: trafficFines.value.map(fine => ({
                reference: fine.reference,
                offence: fine.offence,
                location: fine.location,
                charge: fine.charge,
                penalty: fine.penalty || '0.00', // Ensure penalty exists
                status: fine.status || 'PENDING', // Default status
                issued_date: fine.issued_date,
                operator: fine.operator,
                licence: fine.licence || null,
                pendate: fine.pendate || null
            }))
        };

        const response = await axios.post(`${BASE_URL}/traffic-fines/store/${vehiclePlate.value}`, payload);

        if (response.data.status === 'success') {
            showSuccessAlert(`${response.data.count} fines saved successfully`)
            closeModal();
            await loadTrafficFines(); // Refresh the main table
        } else {
            throw new Error(response.data.message || 'Failed to save fines');
        }
    } catch (error) {
        console.error('Error saving vehicle fines:', error);
        showErrorAlert(error.response?.data?.message || error.message || 'Failed to save vehicle fines');
    } finally {
        isSaving.value = false;
    }
};

// const searchVehicleFines = async () => {
//     try {
//         searchLoading.value = true;
//         searchResults.value = null;

//         const response = await axios.get(`${BASE_URL}/traffic-fines/search`, {
//             params: {
//                 plate_number: searchPlate.value,
//                 with_pending: true,
//                 with_paid: true,
//                 with_unpaid: true
//                 // Add date range if needed
//             }
//         });

//         searchResults.value = response.data;
//     } catch (error) {
//         console.error('Search error:', error);
//         Swal.fire('Error', 'Failed to search vehicle fines', 'error');
//     } finally {
//         searchLoading.value = false;
//     }
// };

// Close modal and reset form

const searchVehicleFines = async (vehicle, dateRange = null) => {
    try {
        searchLoading.value = true;
        const params = {};
        if (dateRange) {
            params.start_date = dateRange.start;
            params.end_date = dateRange.end;
        }

        const response = await axios.get(`${BASE_URL}/traffic-fines/print/${vehicle}`,
            { params, responseType: 'blob' }
        );

        // Create PDF blob and open in new tab
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
    } catch (error) {
        console.error('Error generating PDF:', error);
        showErrorAlert('Failed to generate PDF report');
    } finally {
        searchLoading.value = false;
    }
};

const closeModal = () => {
    showModal.value = false;
    vehiclePlate.value = '';
    assignedRoute.value = '';
    selectedDriver.value = null;
    totalPendingAmount.value = 0;
    searched.value = false;
};

const clearFilters = () => {
    searchPlate.value = '';
    searchQuery.value = '';
    penaltyFilter.value = 'all';
    startDate.value = '';
    endDate.value = '';
    statusFilter.value = '';
    manyFinesFilter.value = '';
    perPage.value = 20;
    currentPage.value = 1; // Reset to first page
};

onMounted(async () => {
    await Promise.all([
        loadDrivers(),
        loadTrafficFines()
    ]);
});
</script>